import apiService from "@/services/apiService";
import { preprocessImage } from "@/utils/imageOptimizationUtility";

// Cache configuration
const CACHE_TTL_MS = 1 * 60 * 1000; // 10 minutes
let refreshTimer = null;
let fetchCount = 0; // Track API calls

/**
 * Normalize listing data for consistent structure
 */
function normalizeListing(listing) {
  
  if (!listing || typeof listing !== "object") {
    return {};
  }

  const isFeatured =
    listing.isFeatured === "1" ||
    listing.isFeatured === 1 ||
    listing.isFeatured === true;
  const tags = Array.isArray(listing.tags)
    ? listing.tags
    : typeof listing.tags === "string"
    ? listing.tags.split(",").map((tag) => tag.trim())
    : [];
  const price = parseFloat(listing.price) || 0;

  // const images = Array.isArray(listing.images)
  //   ? listing.images
  //   : Array.isArray(listing.image_urls)
  //     ? listing.image_urls
  //     : Array.isArray(listing.files)
  //       ? listing.files.map(file => file.url || file.file_path)
  //       : [];

  const images = Array.isArray(listing.image_urls)
    ? listing.image_urls.map(
        (url) => preprocessImage(url, { responsive: true }).thumbnailUrl
      )
    : Array.isArray(listing.images)
    ? listing.images.map(
        (url) => preprocessImage(url, { responsive: true }).thumbnailUrl
      )
    : [];

  const group =
    listing.group ||
    (listing.details && listing.details.group) ||
    (listing.job && listing.job.group) ||
    (listing.job_details && listing.job_details.group);

  const group_id =
    listing.group_id ||
    (listing.details && listing.details.group_id) ||
    (listing.job && listing.job.group_id) ||
    (listing.job_details && listing.job_details.group_id);

  return {
    ...listing,
    title: listing.title || "",
    description: listing.description || "",
    tags,
    price,
    isFeatured,
    status: listing.status || 0,
    verified: !!listing.verified,
    city_name: listing.city_name || listing.city?.city || "",
    category: listing.category || listing.category_name || null,
    subcategory:
      listing.sub_category ||
      listing.subcategory ||
      listing.subcategory_name ||
      null,

    group: group || null,
    group_id: group_id || null,
    city: listing.city || listing.city_name || null,
    images,
  };
}

// put these small helpers near the top of the file (outside export default)
const normalizeCategory = (cat) => {
  if (!cat) return null;
  if (typeof cat === "object") return cat?.id ? cat : null;
  return { id: Number(cat) };
};

const normalizeSubcategory = (sub) => {
  if (!sub) return null;
  if (typeof sub === "object") {
    return sub.id || sub.name || sub.title ? sub : null;
  }
  return { id: Number(sub) };
};

const normalizeCities = (arr) =>
  Array.isArray(arr)
    ? arr
        .filter((c) => c && typeof c === "object")
        .map((c) => ({
          id: c.id ?? c.value ?? c.slug ?? c.name,
          name: c.name ?? c.city ?? c.label ?? "",
          slug:
            c.slug ??
            String(c.value ?? c.name ?? "")
              .toLowerCase()
              .replace(/\s+/g, "-"),
        }))
        .filter((c) => c.name)
    : [];

export default {
  namespaced: true,

  state: {
    // Core data
    allListings: [],
    selectedListingItem: null,
    similarSelectedListings: [],

    // Filters
    filters: {
      category: null,
      subcategory: null,
      group: null,
      query: "",
      keyword: "",
      location: null,
      city: [],
      priceRange: { min: null, max: null },
      bedrooms: { min: null, max: null },
      bathrooms: { min: null, max: null },
      amenities: [],

      // Advanced filters
      verified: null,
      isFeatured: null,
      status: null,
      currency: null,
      datePosted: null,
      priceMin: null,
      priceMax: null,
      sortBy: "newest",
      // ✅ NEW: Job-specific filters
      salaryMin: null,
      salaryMax: null,
      salaryRange: { min: null, max: null },
      experienceLevel: null,
      employmentType: null,
      companyName: "",
      jobTitle: "",
      remote: null,
      skills: [],
      benefits: [],
    },

    // State management
    loading: false,
    error: null,
    lastFetch: 0,
    cacheValid: true,

    // Pagination
    pagination: {
      currentPage: 1,
      totalPages: 1,
      perPage: 20,
      total: 0,
    },
  },

  mutations: {
    SET_ALL_LISTINGS(state, listings) {
      state.allListings = listings;
      state.lastFetch = Date.now();
      state.cacheValid = true;
    },

    // SET_FILTERS(state, filters) {
    //   // Clean subcategory string conversion
    //   if (typeof filters.subcategory === "string") {
    //     filters.subcategory = {
    //       id: parseInt(filters.subcategory),
    //       name: "",
    //     };
    //   }
    //   state.filters = { ...state.filters, ...filters };
    // },

    SET_FILTERS(state, filters) {
      // Ensure we always merge into a plain object
      const base =
        state.filters && typeof state.filters === "object" ? state.filters : {};

      // Clean subcategory string conversion
      if (typeof filters.subcategory === "string") {
        filters.subcategory = {
          id: parseInt(filters.subcategory),
          name: "",
        };
      }

      state.filters = { ...base, ...filters };
    },

    SET_SELECTED_LISTING_ITEM(state, item) {
     
      state.selectedListingItem = item ? normalizeListing(item) : null;
    },

    UPDATE_SELECTED_LISTING_APPLICATION(state, { hasApplied, newCount }) {
      if (state.selectedListingItem?.job) {
        state.selectedListingItem.has_applied = hasApplied;
        state.selectedListingItem.job_applications_count = newCount;
      }
    },

    SET_SIMILAR_SELECTED_LISTINGS(state, listings) {
      state.similarSelectedListings = Array.isArray(listings)
        ? listings.map((listing) => normalizeListing(listing))
        : [];
    },

    SET_LOADING(state, status) {
      state.loading = status;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    CLEAR_ERROR(state) {
      state.error = null;
    },

    SET_CACHE_VALID(state, isValid) {
      state.cacheValid = isValid;
    },

    SET_PAGINATION(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },
  },

  actions: {
    /**
     * Initialize store with listings data
     */
    async initialize({ commit, dispatch, state }, forceRefresh = false) {
      const cacheAge = Date.now() - state.lastFetch;
      const cacheExpired = cacheAge > CACHE_TTL_MS;
      const needsInitialLoad = state.allListings.length === 0;

      // Use cache if valid and not expired
      if (
        !forceRefresh &&
        !needsInitialLoad &&
        !cacheExpired &&
        state.cacheValid
      ) {
        return;
      }

      commit("SET_LOADING", true);
      commit("CLEAR_ERROR");

      try {
        fetchCount++;

        const response = await apiService.get("all-listings");

        // ✅ FIXED: Correct response structure detection for Laravel API

        let listings;

        // 1. Your structure: response.data.original.data (array)
        if (
          response?.data?.original?.data &&
          Array.isArray(response.data.original.data)
        ) {
          listings = response.data.original.data;
        }
        // 2. Laravel: response.data.data (array)
        else if (response?.data?.data && Array.isArray(response.data.data)) {
          listings = response.data.data;
        }
        // 3. Laravel: response.data (array)
        else if (response?.data && Array.isArray(response.data)) {
          listings = response.data;
        } else {
          // Enhanced error logging
          console.error("❌ Failed to extract listings from response:", {
            responseKeys: Object.keys(response || {}),
            dataKeys: response?.data ? Object.keys(response.data) : null,
            originalKeys: response?.data?.original
              ? Object.keys(response.data.original)
              : null,
            fullResponse: response,
          });
          throw new Error(
            "Invalid response format: could not find listings array"
          );
        }

        if (!Array.isArray(listings)) {
          console.error("❌ Extracted listings is not an array:", {
            listingsType: typeof listings,
            listingsValue: listings,
          });
          throw new Error(
            "Invalid response format: expected array of listings"
          );
        }

        // ✅ Enhanced logging by category
        const categoryBreakdown = listings.reduce((acc, listing) => {
          const catId = listing.category_id;
          const catName = listing.category?.name || "Unknown";
          if (!acc[catId]) {
            acc[catId] = { id: catId, name: catName, count: 0 };
          }
          acc[catId].count++;
          return acc;
        }, {});

        // Normalize all listings
        const normalizedListings = listings.map((listing) =>
          normalizeListing(listing)
        );

        commit("SET_ALL_LISTINGS", normalizedListings);
        commit("SET_PAGINATION", {
          total: normalizedListings.length,
          totalPages: Math.ceil(
            normalizedListings.length / state.pagination.perPage
          ),
        });

        // Setup automatic refresh
        dispatch("setupPeriodicRefresh");
      } catch (error) {
        console.error("❌ FilterStore: Failed to initialize", error);
        commit("SET_ERROR", "Failed to fetch listings.");
        commit("SET_CACHE_VALID", false);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    /**
     * Setup periodic refresh timer
     */
    setupPeriodicRefresh({ dispatch }) {
      if (refreshTimer) clearInterval(refreshTimer);

      refreshTimer = setInterval(() => {
        dispatch("refreshAllListings");
      }, CACHE_TTL_MS);
    },

    /**
     * Refresh listings data
     */
    async refreshAllListings({ commit, state }) {
      // Skip if already loading or recently fetched
      if (state.loading) return;

      const timeSinceLastFetch = Date.now() - state.lastFetch;
      if (timeSinceLastFetch < 30000) {
        // Minimum 30 seconds between refreshes
        return;
      }

      try {
        fetchCount++;
      
        const response = await apiService.get("all-listings");

    

        // ✅ FIX: Updated extraction logic to match the actual response structure
        let listings;

        if (
          response?.data?.original?.data &&
          Array.isArray(response.data.original.data)
        ) {
          listings = response.data.original.data;
          
        } else if (
          response?.original?.data &&
          Array.isArray(response.original.data)
        ) {
          listings = response.original.data;
        
        } else if (
          response?.original?.data?.data &&
          Array.isArray(response.original.data.data)
        ) {
          listings = response.original.data.data;
          
        } else if (response?.data && Array.isArray(response.data)) {
          listings = response.data;
         
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          listings = response.data.data;
          
        } else {
          // ✅ Enhanced error logging
          console.error("❌ Failed to extract listings during refresh:", {
            responseStructure: response,
            originalData: response?.original?.data,
            originalDataType: typeof response?.original?.data,
            isArray: Array.isArray(response?.original?.data),
          });
          throw new Error(
            "Invalid response format during refresh: could not find listings array"
          );
        }

        if (!Array.isArray(listings)) {
          console.error("❌ Extracted data is not an array during refresh:", {
            listingsType: typeof listings,
            listingsValue: listings,
          });
          throw new Error(
            `Invalid response format: expected array, got ${typeof listings}`
          );
        }


        const normalizedListings = listings.map((listing) =>
          normalizeListing(listing)
        );

        commit("SET_ALL_LISTINGS", normalizedListings);
        commit("CLEAR_ERROR");
        commit("SET_PAGINATION", {
          total: normalizedListings.length,
          totalPages: Math.ceil(
            normalizedListings.length / state.pagination.perPage
          ),
        });
      } catch (error) {
        console.error("❌ FilterStore: Failed to refresh listings", error);
        console.error("❌ Error details:", {
          message: error.message,
          stack: error.stack,
        });
        commit("SET_ERROR", "Failed to refresh listings.");
      }
    },

    /**
     * Update filters
     */
    // updateFilters({ commit, state }, filters) {
    //   // Clean city filters
    //   if (filters.city) {
    //     filters.city = filters.city.filter(
    //       (city) =>
    //         city && typeof city === "object" && typeof city.name === "string"
    //     );
    //   }

    //   // Handle category reset - clear subcategory when category changes
    //   if (
    //     (filters.category === null || filters.category === undefined) &&
    //     state.filters.subcategory
    //   ) {
    //     filters.subcategory = null;
    //   }

    //   // Clean empty subcategory objects
    //   if (
    //     filters.subcategory &&
    //     typeof filters.subcategory === "object" &&
    //     !filters.subcategory.id &&
    //     !filters.subcategory.name
    //   ) {
    //     filters.subcategory = null;
    //   }

    //   // Update filters in state
    //   Object.keys(filters).forEach((key) => {
    //     if (filters[key] !== undefined) {
    //       commit("SET_FILTERS", { [key]: filters[key] });
    //     }
    //   });
    // },

    // inside export default { actions: { ... } }
    updateFilters({ commit, state, rootState }, partial = {}) {
      // Work on a safe copy
      const next = { ...partial };

      // Accept "keyword" from UI and map it to "query" if query not provided
      if (next.keyword !== undefined && next.query === undefined) {
        next.query = typeof next.keyword === "string" ? next.keyword : "";
      }

      // Normalize cities
      if (next.city !== undefined) {
        next.city = normalizeCities(next.city);
      }

      // Normalize category
      const prevCatId = state.filters.category?.id ?? null;
      if (next.category !== undefined) {
        next.category = normalizeCategory(next.category);
      }
      const nextCatId =
        next.category === null ? null : next.category?.id ?? prevCatId;

      // Normalize subcategory (may be cleared below)
      if (next.subcategory !== undefined) {
        next.subcategory = normalizeSubcategory(next.subcategory);
      }

      // If the category actually changed (including being cleared), drop subcategory
      const categoryChanged =
        next.category !== undefined &&
        String(prevCatId ?? "") !== String(nextCatId ?? "");
      if (categoryChanged) {
        next.subcategory = null;
      } else if (next.subcategory) {
        // Optional: validate subcategory belongs to current category if we have them loaded
        const subs = rootState?.NewCategory?.subcategories || [];
        const subOk =
          subs.length === 0 ||
          subs.some((s) => String(s.id) === String(next.subcategory.id));
        if (!subOk) next.subcategory = null;
      }

      // Commit only provided keys
      Object.keys(next).forEach((key) => {
        if (next[key] !== undefined) {
          commit("SET_FILTERS", { [key]: next[key] });
        }
      });
    },

    /**
     * Set selected listing item
     */
    setSelectedListingItem({ commit }, item) {
      commit("SET_SELECTED_LISTING_ITEM", item);
    },

    /**
     * Set similar listings
     */
    setSimilarSelectedListings({ commit }, listings) {
      commit("SET_SIMILAR_SELECTED_LISTINGS", listings);
    },

    /**
     * Reset all filters to default
     */
    resetFilters({ commit }) {
      const defaultFilters = {
        category: null,
        subcategory: null,
        group: null,
        query: "",
        keyword: "",
        location: null,
        city: [],
        priceRange: { min: null, max: null },
        bedrooms: { min: null, max: null },
        bathrooms: { min: null, max: null },
        amenities: [],
        verified: null,
        isFeatured: null,
        status: null,
        currency: null,
        datePosted: null,
        priceMin: null,
        priceMax: null,
        sortBy: "newest",
        // ✅ ADD: Job-specific filters
        salaryMin: null,
        salaryMax: null,
        salaryRange: { min: null, max: null },
        experienceLevel: null,
        employmentType: null,
        companyName: "",
        jobTitle: "",
        remote: null,
        skills: [],
        benefits: [],
      };

      commit("SET_FILTERS", defaultFilters);

      // ✅ ADDITIONAL: Clear selected listing item when resetting filters
      commit("SET_SELECTED_LISTING_ITEM", null);
    },

    /**
     * Set pagination page
     */
    setPage({ commit }, page) {
      commit("SET_PAGINATION", { currentPage: page });
    },
  },

  getters: {
    /**
     * Get filtered listings based on current filters
     */
    // filteredListings:
    //   (state) =>
    //   (additionalFilters = {}) => {
    //     const combinedFilters = { ...state.filters, ...additionalFilters };
    //     return state.allListings.filter((item) =>
    //       filterItem(item, combinedFilters)
    //     );
    //   },

    filteredListings:
      (state) =>
      (additionalFilters = {}) => {
        const combinedFilters = { ...state.filters, ...additionalFilters };

        // Show all listings if ALL filters are empty/default (no query, no category, etc.)
        const isAllFiltersEmpty = Object.entries(combinedFilters).every(
          ([key, val]) => {
            // Don't count sortBy, page, pageSize as filters
            if (["sortBy", "page", "pageSize"].includes(key)) return true;
            if (val === null || val === undefined) return true;
            if (typeof val === "string" && val.trim() === "") return true;
            if (Array.isArray(val) && val.length === 0) return true;
            if (
              typeof val === "object" &&
              val !== null &&
              Object.keys(val).length === 0
            )
              return true;
            return false;
          }
        );

        if (isAllFiltersEmpty) {
          return state.allListings;
        }

        return state.allListings.filter((item) =>
          filterItem(item, combinedFilters)
        );
      },

    /**
     * Get all listings without filtering
     */
    allListings: (state) => state.allListings || [],

    /**
     * Get current filters
     */
    filters: (state) => state.filters,

    /**
     * Get selected listing item
     */
    selectedListingItem: (state) => state.selectedListingItem,

    /**
     * Get similar listings
     */
    similarSelectedListings: (state) => state.similarSelectedListings,

    /**
     * Check if loading
     */
    isLoading: (state) => state.loading,

    /**
     * Get error message
     */
    errorMessage: (state) => state.error,

    /**
     * Check if any filters are active
     */
    hasActiveFilters: (state) => {
      return (
        state.filters.query !== "" ||
        state.filters.category !== null ||
        state.filters.subcategory !== null ||
        state.filters.location !== null ||
        state.filters.city.length > 0 ||
        state.filters.priceRange.min !== null ||
        state.filters.priceRange.max !== null ||
        state.filters.verified !== null ||
        state.filters.isFeatured !== null ||
        state.filters.status !== null
      );
    },

    /**
     * Get available categories from all listings
     */
    availableCategories: (state) => {
      const categories = {};
      state.allListings.forEach((listing) => {
        if (listing.category && listing.category.name) {
          categories[listing.category.name] = listing.category;
        }
      });
      return Object.values(categories);
    },

    /**
     * Get available subcategories for selected category
     */
    availableSubcategories: (state) => {
      if (!state.filters.category) return [];

      const subcategories = {};
      state.allListings
        .filter(
          (listing) => listing.category?.name === state.filters.category.name
        )
        .forEach((listing) => {
          if (listing.subcategory && listing.subcategory.name) {
            subcategories[listing.subcategory.name] = listing.subcategory;
          }
        });
      return Object.values(subcategories);
    },

    /**
     * Get available cities from all listings
     */
    availableCities: (state) => {
      const cities = {};
      state.allListings.forEach((listing) => {
        if (listing.city && listing.city.name) {
          cities[listing.city.name] = listing.city;
        }
      });
      return Object.values(cities);
    },
  },
};

// Filtering Functions

/**
 * Main filter function - determines if item matches all active filters
 */
function filterItem(item, filters) {
  const itemCategoryId = item.category?.id || item.category_id;
  const itemSubcategoryId =
    item.subcategory?.id || item.sub_category_id || item.subcategory_id;

  // ✅ ENHANCED: Exclude "I want a job" subcategory (ID 17) from all results
  if (itemSubcategoryId == 17 || itemSubcategoryId == "17") {
    
    return false;
  }



  // Get active filter keys (filters with actual values)
  const activeFilterKeys = Object.keys(filters).filter((key) => {
    const value = filters[key];
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) return value.length > 0;
      return !!(value.id || value.name || value.title);
    }
    return value !== null && value !== undefined && value !== "";
  });

  // Case 1: Query-only search
  if (activeFilterKeys.length === 1 && activeFilterKeys[0] === "query") {
    return matchesQuery(item, filters.query);
  }

  // Case 2: Query + Category search
  if (
    activeFilterKeys.length === 2 &&
    activeFilterKeys.includes("query") &&
    activeFilterKeys.includes("category")
  ) {
    return (
      matchesQuery(item, filters.query) &&
      matchesCategory(item, filters.category)
    );
  }

  // Case 3: Full filtering - check all active filters
  if (filters.query && !matchesQuery(item, filters.query)) return false;
  if (filters.category && !matchesCategory(item, filters.category))
    return false;

  // Only check subcategory if explicitly set with valid data
  if (
    filters.subcategory &&
    typeof filters.subcategory === "object" &&
    (filters.subcategory.id ||
      filters.subcategory.title ||
      filters.subcategory.name)
  ) {
    if (!matchesSubcategory(item, filters.subcategory)) return false;
  }

  // Only check group if explicitly set with valid ID
  if (
    filters.group &&
    typeof filters.group === "object" &&
    filters.group.id !== null &&
    filters.group.id !== undefined
  ) {
    if (!matchesGroup(item, filters.group)) return false;
  }

  if (filters.city && Array.isArray(filters.city) && filters.city.length > 0) {
    if (!matchesLocation(item, filters.city)) return false;
  }

  if (filters.priceRange && !matchesPriceRange(item, filters.priceRange))
    return false;
  if (
    filters.priceMin !== null &&
    filters.priceMin !== undefined &&
    !matchesPriceMin(item, filters.priceMin)
  )
    return false;
  if (
    filters.priceMax !== null &&
    filters.priceMax !== undefined &&
    !matchesPriceMax(item, filters.priceMax)
  )
    return false;
  if (
    filters.verified !== null &&
    filters.verified !== undefined &&
    !matchesVerified(item, filters.verified)
  )
    return false;
  if (
    filters.isFeatured !== null &&
    filters.isFeatured !== undefined &&
    !matchesFeatured(item, filters.isFeatured)
  )
    return false;
  if (
    filters.status !== null &&
    filters.status !== undefined &&
    !matchesStatus(item, filters.status)
  )
    return false;
  if (filters.currency && !matchesCurrency(item, filters.currency))
    return false;
  if (filters.datePosted && !matchesDatePosted(item, filters.datePosted))
    return false;

  // ✅ ADD: Job-specific filters
  if (filters.salaryRange && !matchesSalaryRange(item, filters.salaryRange))
    return false;
  if (
    filters.salaryMin !== null &&
    filters.salaryMin !== undefined &&
    !matchesSalaryRange(item, { min: filters.salaryMin, max: null })
  )
    return false;
  if (
    filters.salaryMax !== null &&
    filters.salaryMax !== undefined &&
    !matchesSalaryRange(item, { min: null, max: filters.salaryMax })
  )
    return false;
  if (
    filters.employmentType &&
    !matchesEmploymentType(item, filters.employmentType)
  )
    return false;
  if (filters.companyName && !matchesCompanyName(item, filters.companyName))
    return false;
  if (
    filters.remote !== null &&
    filters.remote !== undefined &&
    !matchesRemoteWork(item, filters.remote)
  )
    return false;

  return true;
}

/**
 * Check if item matches search query
 */


function matchesQuery(item, query) {
  
  if (!query) return true;

  const searchTerm = query.toLowerCase().trim();

  // Single character search
  if (searchTerm.length === 1) {
    return (
      (item.title || "").toLowerCase().includes(searchTerm) ||
      (item.description || "").toLowerCase().includes(searchTerm) ||
      (Array.isArray(item.tags) &&
        item.tags.some((tag) =>
          (tag || "").toLowerCase().includes(searchTerm)
        )) ||
      // ADDED: Check makeModel for single character in vehicles
      (isVehicleSubcategory(item) &&
        (item.car_details?.makeModel || "").toLowerCase().includes(searchTerm))
    );
  }

  // Multi-character search with tokens
  const queryTokens = searchTerm
    .split(/\s+/)
    .filter((token) => token.length >= 2);
  if (queryTokens.length === 0) return true;

  const searchFields = [
    item.title || "",
    item.description || "",
    Array.isArray(item.tags) ? item.tags.join(" ") : "",
    // ADDED: Include makeModel in search fields for vehicles
    ...(isVehicleSubcategory(item) ? [item.car_details?.makeModel || ""] : []),
  ];

  return queryTokens.some((token) =>
    searchFields.some((field) => field.toLowerCase().includes(token))
  );
}

function isVehicleSubcategory(item) {
  const subcategoryId = item.subcategory.id || item.sub_category_id;
  return subcategoryId === 1; // Motors category ID
}

// function matchesMakeModel(item, makeModelQuery) {
//   if (!makeModelQuery) return true;
  
//   const searchTerm = makeModelQuery.toLowerCase().trim();
//   if (searchTerm.length < 2) return true; // Minimum 2 characters for make/model search
  
//   const itemMakeModel = (item.car_details?.makeModel || "").toLowerCase();
//   if (!itemMakeModel) return false;

//   // Split search term into tokens for better matching
//   const searchTokens = searchTerm.split(/\s+/).filter(token => token.length >= 2);
  
//   if (searchTokens.length === 0) return true;
  
//   // Check if all search tokens are found in makeModel
//   return searchTokens.every(token => itemMakeModel.includes(token));
// }

/**
 * Check if item matches category filter
 */
function matchesCategory(item, category) {
  if (!category) return true;

  // Match by ID (preferred)
  if (
    category.id &&
    (item.category_id || (item.category && item.category.id))
  ) {
    const categoryId = item.category_id || (item.category && item.category.id);
    return Number(categoryId) === Number(category.id);
  }

  // Match by name (fallback)
  const itemCategory =
    typeof item.category === "string"
      ? item.category
      : item.category?.name || "";
  const categoryName =
    typeof category === "string" ? category : category?.name || "";
  return itemCategory.toLowerCase() === categoryName.toLowerCase();
}

/**
 * Check if item matches subcategory filter
 */
function matchesSubcategory(item, subcategory) {
  if (!subcategory) return true;

  // Skip empty objects
  if (
    typeof subcategory === "object" &&
    !subcategory.id &&
    !subcategory.title &&
    !subcategory.name
  ) {
    return true;
  }

  // Match by ID (preferred)
  const itemId =
    item.subcategory?.id ||
    item.sub_category?.id ||
    item.subcategory_id ||
    item.sub_category_id;
  const filterId =
    typeof subcategory === "object" ? subcategory.id : subcategory;

  if (itemId !== undefined && filterId !== undefined) {
    return String(itemId) === String(filterId);
  }

  // Match by name/title (fallback)
  const itemValues = [
    typeof item.subcategory === "string" ? item.subcategory : null,
    item.subcategory?.title,
    item.subcategory?.name,
    item.sub_category?.title,
    item.sub_category?.name,
  ].filter(Boolean);

  const filterValues = [
    typeof subcategory === "string" ? subcategory : null,
    subcategory.title,
    subcategory.name,
  ].filter(Boolean);

  return itemValues.some((itemValue) =>
    filterValues.some(
      (filterValue) => itemValue.toLowerCase() === filterValue.toLowerCase()
    )
  );
}

/**
 * Check if item matches group filter
 */
function matchesGroup(item, group) {
  if (!group) return true;

  const groupId = typeof group === "object" ? group.id : group;
  const itemGroupId = item.group?.id || item.group_id;

  if (!itemGroupId && groupId) return false;

  return itemGroupId == groupId;
}

/**
 * Check if item matches group filter
 */


/**
 * Check if item matches location filter
 */
function matchesLocation(item, cityFilter) {
  if (!cityFilter || !cityFilter.length) return true;

  const itemCityName = (item?.city_name || "").toLowerCase();
  const validCityFilter = cityFilter.filter(
    (city) => city && typeof city === "object" && typeof city.name === "string"
  );

  return validCityFilter.some(
    (city) => city.name.toLowerCase() === itemCityName
  );
}

/**
 * Check if item matches price range filter
 */
function matchesPriceRange(item, priceRange) {
  if (!priceRange) return true;

  const hasActiveFilter =
    (priceRange.min !== undefined && priceRange.min !== null) ||
    (priceRange.max !== undefined && priceRange.max !== null) ||
    (typeof priceRange.value === "string" && priceRange.value.includes("-"));

  if (!hasActiveFilter) return true;

  let min = Number.NEGATIVE_INFINITY;
  let max = Number.POSITIVE_INFINITY;

  if (typeof priceRange.value === "string" && priceRange.value.includes("-")) {
    const [minStr, maxStr] = priceRange.value.split("-");
    min = minStr ? parseFloat(minStr) : Number.NEGATIVE_INFINITY;
    max = maxStr ? parseFloat(maxStr) : Number.POSITIVE_INFINITY;
  } else {
    if (priceRange.min !== undefined && priceRange.min !== null)
      min = parseFloat(priceRange.min);
    if (priceRange.max !== undefined && priceRange.max !== null)
      max = parseFloat(priceRange.max);
  }

  const price = parseFloat(item.price);
  if (isNaN(price))
    return !(priceRange.min !== null || priceRange.max !== null);

  return price >= min && price <= max;
}

// Additional filter functions
function matchesPriceMin(item, priceMin) {
  if (priceMin === null || priceMin === undefined) return true;
  const price = parseFloat(item.price);
  return !isNaN(price) && price >= priceMin;
}

function matchesPriceMax(item, priceMax) {
  if (priceMax === null || priceMax === undefined) return true;
  const price = parseFloat(item.price);
  return !isNaN(price) && price <= priceMax;
}

function matchesVerified(item, verified) {
  if (verified === null || verified === undefined) return true;
  return Boolean(item.verified) === Boolean(verified);
}

function matchesFeatured(item, isFeatured) {
  if (isFeatured === null || isFeatured === undefined) return true;
  return String(item.isFeatured) === String(isFeatured);
}

function matchesStatus(item, status) {
  if (status === null || status === undefined) return true;
  return Number(item.status) === Number(status);
}

function matchesCurrency(item, currency) {
  if (!currency) return true;
  return item.currency === currency;
}

function matchesDatePosted(item, datePosted) {
  if (!datePosted) return true;

  const itemDate = new Date(item.created_at);
  const now = new Date();

  switch (datePosted) {
    case "today":
      return itemDate.toDateString() === now.toDateString();
    case "this_week":
      return itemDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case "this_month":
      return (
        itemDate.getMonth() === now.getMonth() &&
        itemDate.getFullYear() === now.getFullYear()
      );
    case "last_30_days":
      return itemDate >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    case "last_90_days":
      return itemDate >= new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    case "this_year":
      return itemDate.getFullYear() === now.getFullYear();
    default:
      return true;
  }
}

/**
 * Check if item matches salary range filter (for jobs)
 */
function matchesSalaryRange(item, salaryRange) {
  if (!salaryRange || (!salaryRange.min && !salaryRange.max)) return true;

  // Only apply to job listings
  if (item.category_id !== 2) return true;

  const jobSalaryMin = parseFloat(
    item.job?.salary_min || item.job_details?.salary_min || 0
  );
  const jobSalaryMax = parseFloat(
    item.job?.salary_max || item.job_details?.salary_max || 0
  );

  const filterMin = salaryRange.min || 0;
  const filterMax = salaryRange.max || Number.POSITIVE_INFINITY;

  // Check if job salary range overlaps with filter range
  return jobSalaryMax >= filterMin && jobSalaryMin <= filterMax;
}

/**
 * Check if item matches employment type filter (for jobs)
 */
function matchesEmploymentType(item, employmentType) {
  if (!employmentType) return true;

  // Only apply to job listings
  if (item.category_id !== 2) return true;

  const jobEmploymentType =
    item.job?.employment_type || item.job_details?.employment_type;
  return (
    jobEmploymentType &&
    jobEmploymentType.toLowerCase() === employmentType.toLowerCase()
  );
}

/**
 * Check if item matches company name filter (for jobs)
 */
function matchesCompanyName(item, companyName) {
  if (!companyName) return true;

  // Only apply to job listings
  if (item.category_id !== 2) return true;

  const jobCompanyName =
    item.job?.company_name || item.job_details?.company_name || "";
  return jobCompanyName.toLowerCase().includes(companyName.toLowerCase());
}

/**
 * Check if item matches remote work filter (for jobs)
 */
function matchesRemoteWork(item, remote) {
  if (remote === null || remote === undefined) return true;

  // Only apply to job listings
  if (item.category_id !== 2) return true;

  const jobRemote = item.job?.remote || item.job_details?.remote;
  return Boolean(jobRemote) === Boolean(remote);
}
